/* eslint-disable jsdoc/require-jsdoc -- typed store operation helpers; names match ActivityEditStore call sites */
import type { ActivityService } from '@features/kt/activity-browser/services/activity.service';

import { httpErrorStatus } from '@shared/utils/http-error-status.util';
import { httpErrorTitle } from '@shared/utils/http-error-title.util';

import {
  type ActivityPedagogicalCustomFieldResponse,
  type CreateActivityPedagogicalCustomFieldRequest,
  type UpdateActivityPedagogicalCustomFieldRequest,
} from '@api';

import { StatusCodes } from 'http-status-codes';

import { CUSTOM_PEDAGOGICAL_FIELD_REORDER_STALE } from '../constants/activity-edit-pedagogical-custom.constants';
import {
  applyCustomPedagogicalFieldMetadataUpdate,
  mergeCustomPedagogicalFieldListFromServer,
  mergeCustomPedagogicalFieldRowFromServer,
  withCustomPedagogicalFieldAtPosition,
} from '../constants/activity-edit-pedagogical-custom.utils';

export type CustomPedagogicalPersistBindings = {
  readCustomFields: () => ActivityPedagogicalCustomFieldResponse[];
  applyCustomFields: (items: ActivityPedagogicalCustomFieldResponse[]) => void;
  setSettled: (items: ActivityPedagogicalCustomFieldResponse[]) => void;
  draftId: () => string | undefined;
  onPersisted: () => void;
};

export function cloneCustomPedagogicalFieldRows(
  items: readonly ActivityPedagogicalCustomFieldResponse[],
): ActivityPedagogicalCustomFieldResponse[] {
  return items.map((item) => ({ ...item }));
}

export function mergeCustomPedagogicalValueFromServer(
  current: readonly ActivityPedagogicalCustomFieldResponse[],
  saved: ActivityPedagogicalCustomFieldResponse,
  committedValue: string | null,
): ActivityPedagogicalCustomFieldResponse[] {
  return mergeCustomPedagogicalFieldRowFromServer(current, saved, committedValue);
}

export function optimisticCustomPedagogicalDelete(
  items: readonly ActivityPedagogicalCustomFieldResponse[],
  fieldId: string,
): ActivityPedagogicalCustomFieldResponse[] {
  return items.filter((item) => item.id !== fieldId);
}

export function optimisticCustomPedagogicalMetadata(
  items: readonly ActivityPedagogicalCustomFieldResponse[],
  fieldId: string,
  body: UpdateActivityPedagogicalCustomFieldRequest,
): ActivityPedagogicalCustomFieldResponse[] {
  return items.map((item) => (item.id === fieldId ? applyCustomPedagogicalFieldMetadataUpdate(item, body) : item));
}

export function optimisticCustomPedagogicalReorder(
  items: readonly ActivityPedagogicalCustomFieldResponse[],
  fieldId: string,
  position: number,
): ActivityPedagogicalCustomFieldResponse[] {
  return withCustomPedagogicalFieldAtPosition(items, fieldId, position);
}

export function optimisticCustomPedagogicalVisibility(
  items: readonly ActivityPedagogicalCustomFieldResponse[],
  fieldId: string,
  visible: boolean,
): ActivityPedagogicalCustomFieldResponse[] {
  return items.map((item) => (item.id === fieldId ? { ...item, visible } : item));
}

export async function persistCreateCustomPedagogicalField(
  activityService: ActivityService,
  id: string,
  request: CreateActivityPedagogicalCustomFieldRequest,
  readCustomFields: () => ActivityPedagogicalCustomFieldResponse[],
  applyCustomFields: (items: ActivityPedagogicalCustomFieldResponse[]) => void,
  setSettled: (items: ActivityPedagogicalCustomFieldResponse[]) => void,
  draftId: () => string | undefined,
  onPersisted: () => void,
): Promise<void> {
  try {
    const list = await activityService.createPedagogicalCustomField(id, request);
    if (draftId() !== id) {
      return;
    }

    const merged = mergeCustomPedagogicalFieldListFromServer(readCustomFields(), list.items);
    setSettled(cloneCustomPedagogicalFieldRows(merged));
    applyCustomFields(merged);
    onPersisted();
  } catch {
    // Create has no optimistic row — leave the draft unchanged on failure.
  }
}

export async function persistCustomPedagogicalReorder(
  activityService: ActivityService,
  id: string,
  fieldId: string,
  position: number,
  baseline: ActivityPedagogicalCustomFieldResponse[],
  readCustomFields: () => ActivityPedagogicalCustomFieldResponse[],
  applyCustomFields: (items: ActivityPedagogicalCustomFieldResponse[]) => void,
  setSettled: (items: ActivityPedagogicalCustomFieldResponse[]) => void,
  draftId: () => string | undefined,
  onPersisted: () => void,
): Promise<void> {
  try {
    const list = await activityService.reorderPedagogicalCustomField(id, fieldId, position);
    const merged = mergeCustomPedagogicalFieldListFromServer(readCustomFields(), list.items);
    setSettled(cloneCustomPedagogicalFieldRows(merged));
    applyCustomFields(merged);
    onPersisted();
  } catch (error) {
    if (draftId() !== id) {
      return;
    }

    if (
      httpErrorStatus(error) === StatusCodes.CONFLICT &&
      httpErrorTitle(error) === CUSTOM_PEDAGOGICAL_FIELD_REORDER_STALE
    ) {
      try {
        const list = await activityService.getPedagogicalCustomFields(id);
        const merged = mergeCustomPedagogicalFieldListFromServer(readCustomFields(), list.items);
        setSettled(cloneCustomPedagogicalFieldRows(merged));
        applyCustomFields(merged);
        onPersisted();
        return;
      } catch {
        applyCustomFields(cloneCustomPedagogicalFieldRows(baseline));
        return;
      }
    }

    applyCustomFields(cloneCustomPedagogicalFieldRows(baseline));
  }
}

export async function persistCustomPedagogicalVisibility(
  activityService: ActivityService,
  id: string,
  fieldId: string,
  visible: boolean,
  baseline: ActivityPedagogicalCustomFieldResponse[],
  readCustomFields: () => ActivityPedagogicalCustomFieldResponse[],
  applyCustomFields: (items: ActivityPedagogicalCustomFieldResponse[]) => void,
  setSettled: (items: ActivityPedagogicalCustomFieldResponse[]) => void,
  onPersisted: () => void,
): Promise<void> {
  try {
    const list = await activityService.updatePedagogicalCustomFieldVisibility(id, fieldId, visible);
    const merged = mergeCustomPedagogicalFieldListFromServer(readCustomFields(), list.items);
    setSettled(cloneCustomPedagogicalFieldRows(merged));
    applyCustomFields(merged);
    onPersisted();
  } catch {
    applyCustomFields(cloneCustomPedagogicalFieldRows(baseline));
  }
}

export async function persistDeleteCustomPedagogicalField(
  activityService: ActivityService,
  id: string,
  fieldId: string,
  baseline: ActivityPedagogicalCustomFieldResponse[],
  readCustomFields: () => ActivityPedagogicalCustomFieldResponse[],
  applyCustomFields: (items: ActivityPedagogicalCustomFieldResponse[]) => void,
  setSettled: (items: ActivityPedagogicalCustomFieldResponse[]) => void,
  draftId: () => string | undefined,
  onPersisted: () => void,
): Promise<void> {
  try {
    const list = await activityService.deletePedagogicalCustomField(id, fieldId);
    if (draftId() !== id) {
      return;
    }

    const merged = mergeCustomPedagogicalFieldListFromServer(readCustomFields(), list.items);
    setSettled(cloneCustomPedagogicalFieldRows(merged));
    applyCustomFields(merged);
    onPersisted();
  } catch {
    applyCustomFields(cloneCustomPedagogicalFieldRows(baseline));
  }
}

export async function persistUpdateCustomPedagogicalField(
  activityService: ActivityService,
  id: string,
  fieldId: string,
  body: UpdateActivityPedagogicalCustomFieldRequest,
  baseline: ActivityPedagogicalCustomFieldResponse[],
  readCustomFields: () => ActivityPedagogicalCustomFieldResponse[],
  applyCustomFields: (items: ActivityPedagogicalCustomFieldResponse[]) => void,
  setSettled: (items: ActivityPedagogicalCustomFieldResponse[]) => void,
  draftId: () => string | undefined,
  onPersisted: () => void,
): Promise<void> {
  try {
    const list = await activityService.updatePedagogicalCustomField(id, fieldId, body);
    if (draftId() !== id) {
      return;
    }

    const merged = mergeCustomPedagogicalFieldListFromServer(readCustomFields(), list.items);
    setSettled(cloneCustomPedagogicalFieldRows(merged));
    applyCustomFields(merged);
    onPersisted();
  } catch {
    applyCustomFields(cloneCustomPedagogicalFieldRows(baseline));
  }
}
